import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setloginUserName } from "../selectSlice";
import { fetchCurrentUser } from "../utils/currentUser";
import { purgeLegacyUserStorage } from "../utils/legacyStorage";

// Loads the logged-in user from the API into Redux whenever `enabled` is true
// (i.e. a session exists but the user isn't in the store yet - first load or a
// page refresh). `onUnauthorized` runs if the server says the session is over;
// any other failure is surfaced as `failed` so the UI can offer a retry.
export const useLoadCurrentUser = ({ enabled, onUnauthorized }) => {
  const dispatch = useDispatch();
  const inFlight = useRef(false);
  const unauthorizedRef = useRef(onUnauthorized);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    unauthorizedRef.current = onUnauthorized;
  }, [onUnauthorized]);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      dispatch(setloginUserName(await fetchCurrentUser()));
      purgeLegacyUserStorage();
    } catch (err) {
      if (err?.response?.status === 401) {
        unauthorizedRef.current?.();
      } else {
        setFailed(true);
      }
    } finally {
      inFlight.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (enabled && !failed) load();
  }, [enabled, failed, load]);

  const retry = useCallback(() => setFailed(false), []);

  return { failed, retry };
};

export default useLoadCurrentUser;
